import { useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

interface UseSessionTimeoutOptions {
  timeoutMinutes?: number;
  warningMinutes?: number;
  onTimeout?: () => void;
}

export const useSessionTimeout = (options: UseSessionTimeoutOptions = {}) => {
  const {
    timeoutMinutes = 120, // Default 120 minutes timeout
    warningMinutes = 60,  // Show warning 60 minutes before timeout
    onTimeout
  } = options;

  const { user, isAdmin, logout } = useAuth();
  const navigate = useNavigate();
  
  const [showWarning, setShowWarning] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);
  
  const lastActivityRef = useRef<Date>(new Date());
  const timeoutIdRef = useRef<NodeJS.Timeout | null>(null);
  const warningTimeoutIdRef = useRef<NodeJS.Timeout | null>(null);
  const intervalIdRef = useRef<NodeJS.Timeout | null>(null);

  // Track user activity
  const updateActivity = useCallback(() => {
    lastActivityRef.current = new Date();
    
    // If warning is showing, hide it and reset timers
    if (showWarning) {
      setShowWarning(false);
      setTimeRemaining(null);
    }
    
    // Reset the timeout
    resetTimeout();
  }, [showWarning]);

  // Handle session timeout
  const handleTimeout = useCallback(async () => {
    console.log('Session timeout - logging out user');
    
    // Call custom handler if provided
    if (onTimeout) {
      onTimeout();
    }
    
    // Logout and redirect
    await logout();
    navigate('/unauthorized?reason=session_expired');
  }, [logout, navigate, onTimeout]);

  // Session extension removed - sessions expire naturally after timeout period

  // Reset timeout
  const resetTimeout = useCallback(() => {
    // Clear existing timeouts
    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
    }
    if (warningTimeoutIdRef.current) {
      clearTimeout(warningTimeoutIdRef.current);
    }
    if (intervalIdRef.current) {
      clearInterval(intervalIdRef.current);
    }

    // Only set timeouts for admin users
    if (!isAdmin || !user) return;

    const timeoutMs = timeoutMinutes * 60 * 1000;
    const warningMs = (timeoutMinutes - warningMinutes) * 60 * 1000;

    // Set warning timeout
    warningTimeoutIdRef.current = setTimeout(() => {
      setShowWarning(true);
      setTimeRemaining(warningMinutes * 60); // seconds
      
      // Start countdown interval
      intervalIdRef.current = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev === null || prev <= 0) {
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }, warningMs);

    // Set final timeout
    timeoutIdRef.current = setTimeout(handleTimeout, timeoutMs);
    
    // Sessions expire naturally - no extension needed
  }, [isAdmin, user, timeoutMinutes, warningMinutes, handleTimeout]);

  // Set up activity listeners
  useEffect(() => {
    if (!isAdmin || !user) return;

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    
    const handleActivity = () => {
      updateActivity();
    };

    // Add event listeners
    events.forEach(event => {
      document.addEventListener(event, handleActivity);
    });

    // Initial setup
    resetTimeout();

    // Cleanup
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
      
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
      }
      if (warningTimeoutIdRef.current) {
        clearTimeout(warningTimeoutIdRef.current);
      }
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
      }
    };
  }, [isAdmin, user, updateActivity, resetTimeout]);

  // Format time for display
  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const dismissWarning = useCallback(() => {
    setShowWarning(false);
    setTimeRemaining(null);
    updateActivity(); // This will reset the timeout
  }, [updateActivity]);

  return {
    showWarning,
    timeRemaining,
    formatTime,
    dismissWarning
  };
};